import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import HelpButton from "./HelpDialog";
import { DIALOG_HEIGHT } from "./dialogSize";
import { useTheme } from "../theme";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";
const PAGE_SIZE = 10;

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

async function fetchJson(url, opts) {
  const res = await fetch(url, opts);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
  return body;
}

// Default in-form value for a fresh variable of a given kind.
function emptyValueForKind(kind) {
  if (kind === "checkbox" || kind === "switch") return false;
  if (kind === "number") return "";
  return "";
}

// Best-effort coercion of a JSON-uploaded value into the shape the form
// expects for a given kind. Returns { value, ok, warning? } — `ok=false`
// means we could not coerce and want to warn the user, but the value is
// still stored (raw) so save proceeds.
function coerceForKind(kind, raw) {
  const k = (kind || "").toLowerCase();
  if (k === "checkbox" || k === "switch") {
    if (typeof raw === "boolean") return { value: raw, ok: true };
    if (typeof raw === "number") return { value: raw !== 0, ok: true };
    if (typeof raw === "string") {
      const s = raw.trim().toLowerCase();
      if (["true", "1", "yes", "y", "on", "t"].includes(s)) return { value: true, ok: true };
      if (["false", "0", "no", "n", "off", "f", ""].includes(s)) return { value: false, ok: true };
    }
    return { value: !!raw, ok: false, warning: "expected true/false" };
  }
  if (k === "number") {
    if (typeof raw === "number" && !Number.isNaN(raw)) return { value: String(raw), ok: true };
    if (typeof raw === "string" && raw.trim() !== "" && !Number.isNaN(Number(raw))) {
      return { value: raw.trim(), ok: true };
    }
    return { value: raw == null ? "" : String(raw), ok: false, warning: "not a number" };
  }
  if (k === "date") {
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      return { value: raw.toISOString().slice(0, 10), ok: true };
    }
    if (typeof raw === "string") {
      const s = raw.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { value: s, ok: true };
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) return { value: d.toISOString().slice(0, 10), ok: true };
    }
    return { value: raw == null ? "" : String(raw), ok: false, warning: "not a valid date" };
  }
  // text / password / textarea / select / radio / field / unknown
  if (raw == null) return { value: "", ok: true };
  if (typeof raw === "boolean") return { value: raw ? "true" : "false", ok: true };
  return { value: String(raw), ok: true };
}

const CUSTOM_WIDGET_KINDS = new Set(["element", "link", "button"]);

// One-time read shim: any datasource saved during the reverted iteration is
// stored as `{schema, rows: [...]}` — surface the first row's values as the
// single flat value set the editor understands. Legacy flat datasources pass
// through unchanged.
function flattenLegacyMatrix(raw) {
  if (!raw || typeof raw !== "object") return {};
  if (Array.isArray(raw.rows) && raw.rows.length) {
    const first = raw.rows[0];
    if (first && typeof first === "object" && first.values && typeof first.values === "object") {
      return { ...first.values };
    }
    return {};
  }
  const { schema: _s, rows: _r, ...rest } = raw;
  return rest;
}

/* ------------------------------------------------------------------ */
/*  Generic modal shell — matches UsersView                             */
/*  `fit`: a confirmation fits its message instead of DIALOG_HEIGHT.   */
/* ------------------------------------------------------------------ */
function Modal({ title, onClose, children, footer, maxWidth = "max-w-lg", fit = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={["relative z-10 flex w-full flex-col overflow-hidden rounded-2xl bg-card shadow-xl ring-1 ring-slate-200", fit ? "max-h-[85vh]" : DIALOG_HEIGHT, maxWidth].join(" ")}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-none justify-end gap-2 border-t border-slate-100 px-5 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Kind-aware variable row — renders the right widget for the variable */
/*  and normalises its `value` shape (bool for checkbox/switch, string  */
/*  everywhere else).                                                   */
/* ------------------------------------------------------------------ */
function VariableRow({ variable, value, onChange }) {
  const kind = (variable.kind || "field").toLowerCase();
  const label = (
    <label className="mb-1 flex items-center gap-2 font-mono text-[11px] text-slate-600">
      <span className="truncate">{variable.param_name}</span>
      <span className="rounded bg-slate-100 px-1 py-0.5 text-[10px] font-medium uppercase text-slate-500">
        {kind}
      </span>
      {variable.field_label && variable.field_label !== variable.param_name && (
        <span className="truncate text-[10px] font-normal normal-case text-slate-400">
          · {variable.field_label}
        </span>
      )}
    </label>
  );

  if (kind === "checkbox" || kind === "switch") {
    const on = value === true || value === "true";
    return (
      <div>
        {label}
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => onChange(!on)}
          className={[
            "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset transition",
            on
              ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
              : "bg-slate-100 text-slate-600 ring-slate-500/20",
          ].join(" ")}
        >
          <span
            className={[
              "inline-block h-2 w-2 rounded-full",
              on ? "bg-emerald-500" : "bg-slate-400",
            ].join(" ")}
          />
          {on ? "true" : "false"}
        </button>
      </div>
    );
  }

  if (kind === "textarea") {
    return (
      <div>
        {label}
        <textarea
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="(prompt at run time if left blank)"
          className={inputCls}
        />
      </div>
    );
  }

  if (kind === "number") {
    return (
      <div>
        {label}
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="(prompt at run time if left blank)"
          className={inputCls}
        />
      </div>
    );
  }

  if (kind === "date") {
    return (
      <div>
        {label}
        <input
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      </div>
    );
  }

  if (kind === "password") {
    return (
      <div>
        {label}
        <input
          type="password"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="(prompt at run time if left blank)"
          autoComplete="new-password"
          className={inputCls}
        />
      </div>
    );
  }

  if (kind === "select" && Array.isArray(variable.options) && variable.options.length > 0) {
    return (
      <div>
        {label}
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        >
          <option value="">— pick —</option>
          {variable.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Custom widgets we can't reliably drive — warn the user.
  const isCustom = CUSTOM_WIDGET_KINDS.has(kind);
  return (
    <div>
      {label}
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="(prompt at run time if left blank)"
        className={inputCls}
      />
      {isCustom && (
        <p className="mt-1 text-[11px] text-amber-700">
          Custom widget — value may not apply reliably at run time.
        </p>
      )}
      {kind === "select" && (
        <p className="mt-1 text-[11px] text-slate-500">
          No captured options — type the option label to match at run time.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Add / Edit datasource modal                                         */
/* ------------------------------------------------------------------ */
function DatasourceFormModal({
  mode,
  initial, // full datasource row when editing; { testcasekey?, testcase_name? } for add
  onClose,
  onSubmit,
  saving,
  error,
}) {
  const isEdit = mode === "edit";
  const [eligible, setEligible] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(!isEdit);
  const [loadError, setLoadError] = useState("");

  const [testcasekey, setTestcasekey] = useState(initial?.testcasekey || "");
  const [name, setName] = useState(initial?.name || "");
  // Read shim: any datasource saved during the reverted matrix iteration was
  // stored as `{schema, rows: [...]}`. Flatten the first row's values on
  // load so old datasources open naturally in the flat editor and get
  // re-saved as a flat dict on the next Save.
  const [values, setValues] = useState(() => flattenLegacyMatrix(initial?.data));
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Feedback banner shown after a JSON upload — reports what was loaded and
  // which keys were skipped so users know why some fields are still blank.
  //   { loaded: number, total: number, unknown: string[], filename: string } | { error: string } | null
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  // Load the eligible test cases (only for Add mode; Edit locks the case).
  useEffect(() => {
    if (isEdit) return undefined;
    let cancelled = false;
    (async () => {
      setEligibleLoading(true);
      setLoadError("");
      try {
        const body = await fetchJson(`${API_BASE_URL}datasources/eligible-testcases`);
        if (cancelled) return;
        setEligible(Array.isArray(body?.data) ? body.data : []);
      } catch (e) {
        if (!cancelled) setLoadError(e.message);
      } finally {
        if (!cancelled) setEligibleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  // When the user picks a test case (or when editing), figure out the variable
  // schema: [{ param_name, kind, field_label, selector, options }]. Edit mode
  // fetches the case's steps directly; Add mode reads it from the
  // eligible-testcases response.
  const [editVariables, setEditVariables] = useState([]);
  const [editKeysLoading, setEditKeysLoading] = useState(false);
  useEffect(() => {
    if (!isEdit || !initial?.testcasekey) return undefined;
    let cancelled = false;
    (async () => {
      setEditKeysLoading(true);
      try {
        const body = await fetchJson(`${API_BASE_URL}testcases/${initial.testcasekey}`);
        if (cancelled) return;
        const tc = body?.data || {};
        const seen = new Map();
        for (const s of tc.steps || []) {
          if (!s?.is_dynamic) continue;
          const n = s.param_name || s.field_label;
          if (!n || seen.has(n)) continue;
          seen.set(n, {
            param_name: n,
            kind: s.kind || "field",
            field_label: s.field_label || null,
            selector: s.selector || null,
            options: [],
          });
        }
        setEditVariables(Array.from(seen.values()));
      } catch {
        // ignore — will show the existing values as-is
      } finally {
        if (!cancelled) setEditKeysLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, initial?.testcasekey]);

  const activeVariables = useMemo(() => {
    if (isEdit) return editVariables;
    const pick = eligible.find((t) => t.testcasekey === testcasekey);
    // Fall back to a synthetic list built from param_names for older backends.
    if (pick?.variables?.length) return pick.variables;
    return (pick?.param_names || []).map((n) => ({
      param_name: n,
      kind: "field",
      field_label: null,
      selector: null,
      options: [],
    }));
  }, [isEdit, editVariables, eligible, testcasekey]);
  const activeKeys = useMemo(
    () => activeVariables.map((v) => v.param_name),
    [activeVariables]
  );

  // When the picked case changes (Add mode), rebuild the values object so
  // stale keys from a previous pick disappear. Preserve existing values for
  // keys that survived.
  useEffect(() => {
    if (isEdit) return;
    setValues((prev) => {
      const next = {};
      for (const v of activeVariables) {
        next[v.param_name] = prev[v.param_name] ?? emptyValueForKind(v.kind);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKeys.join("|")]);

  const errors = {};
  if (!isEdit && !testcasekey) errors.testcasekey = "Pick a test case.";
  if (name.trim().length === 0) errors.name = "Name is required.";
  const canSubmit = Object.keys(errors).length === 0;
  const markTouched = (f) => setTouched((t) => ({ ...t, [f]: true }));
  const showError = (f) => (touched[f] || submitAttempted) && errors[f];

  const submit = () => {
    setSubmitAttempted(true);
    if (!canSubmit || saving) return;
    const payload = { name: name.trim(), data: values };
    if (!isEdit) payload.testcasekey = testcasekey;
    onSubmit(payload);
  };

  const handleFilePicked = (e) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-selected later.
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(String(ev.target?.result || ""));
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("File must contain a JSON object with key/value pairs.");
        }
        const byKey = new Map(activeVariables.map((v) => [v.param_name, v]));
        const nextValues = { ...values };
        let loaded = 0;
        const unknown = [];
        const warnings = []; // [{key, reason}]
        for (const [k, v] of Object.entries(parsed)) {
          const variable = byKey.get(k);
          if (!variable) {
            unknown.push(k);
            continue;
          }
          const { value, ok, warning } = coerceForKind(variable.kind, v);
          nextValues[k] = value;
          if (!ok) warnings.push({ key: k, reason: warning || "type mismatch" });
          loaded += 1;
        }
        // JSON upload applies to the currently-active row only.
        setValues(nextValues);
        setUploadStatus({
          loaded,
          total: activeKeys.length,
          unknown,
          warnings,
          filename: file.name,
        });
      } catch (err) {
        setUploadStatus({ error: err.message || "Invalid JSON file." });
      }
    };
    reader.onerror = () =>
      setUploadStatus({ error: "Could not read the selected file." });
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    if (activeVariables.length === 0) return;
    const stub = {};
    for (const v of activeVariables) {
      // Seed each key with a type-appropriate placeholder so the template
      // hints at what shape to fill in (`false` for a checkbox, `""` for text).
      stub[v.param_name] = emptyValueForKind(v.kind);
    }
    const blob = new Blob([JSON.stringify(stub, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const tcName = isEdit
      ? initial?.testcase_name || "datasource"
      : eligible.find((t) => t.testcasekey === testcasekey)?.name || "datasource";
    const slug = tcName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "datasource";
    a.href = url;
    a.download = `${slug}_template.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";
  const reqMark = <span className="text-red-500">*</span>;
  const errorTextCls = "mt-1 text-xs text-rose-600";
  const disabledInputCls = "cursor-not-allowed bg-slate-100 text-slate-500";

  return (
    <Modal
      title={isEdit ? "Edit Datasource" : "Add Datasource"}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        {loadError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {loadError}
          </div>
        )}

        <div>
          <label className={labelCls}>Test case {reqMark}</label>
          {isEdit ? (
            <input
              value={initial?.testcase_name || initial?.testcasekey || ""}
              disabled
              className={[inputCls, disabledInputCls].join(" ")}
            />
          ) : eligibleLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" /> Loading test cases…
            </div>
          ) : eligible.length === 0 ? (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              No test cases have dynamic steps yet. Record a case and mark at least one step Dynamic first.
            </p>
          ) : (
            <>
              <select
                value={testcasekey}
                onChange={(e) => setTestcasekey(e.target.value)}
                onBlur={() => markTouched("testcasekey")}
                className={inputCls}
              >
                <option value="">— select a test case —</option>
                {eligible.map((t) => (
                  <option key={t.testcasekey} value={t.testcasekey}>
                    {t.name} ({t.param_names.length} variable{t.param_names.length === 1 ? "" : "s"})
                  </option>
                ))}
              </select>
              {showError("testcasekey") && <p className={errorTextCls}>{errors.testcasekey}</p>}
            </>
          )}
        </div>

        <div>
          <label className={labelCls}>Name {reqMark}</label>
          <input
            autoFocus={isEdit}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => markTouched("name")}
            placeholder="e.g. Positive login"
            className={inputCls}
          />
          {showError("name") && <p className={errorTextCls}>{errors.name}</p>}
        </div>

        {activeKeys.length > 0 ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Values</p>
              <div className="flex items-center gap-1.5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  onChange={handleFilePicked}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Load values from a JSON file"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Icons.Plus className="h-3 w-3" />
                  Upload JSON
                </button>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  title="Download an empty JSON template with the expected keys"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Icons.ChevronDown className="h-3 w-3" />
                  Template
                </button>
              </div>
            </div>
            {uploadStatus && (
              uploadStatus.error ? (
                <div className="mb-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
                  {uploadStatus.error}
                </div>
              ) : (
                <div className="mb-2 space-y-1">
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800">
                    Loaded {uploadStatus.loaded} of {uploadStatus.total} variable
                    {uploadStatus.total === 1 ? "" : "s"} from{" "}
                    <span className="font-mono">{uploadStatus.filename}</span>
                    {uploadStatus.unknown.length > 0 && (
                      <>
                        {" "}· ignored{" "}
                        <span className="font-mono">
                          {uploadStatus.unknown.slice(0, 3).join(", ")}
                          {uploadStatus.unknown.length > 3
                            ? `, +${uploadStatus.unknown.length - 3} more`
                            : ""}
                        </span>
                      </>
                    )}
                  </div>
                  {(uploadStatus.warnings || []).length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                      Type mismatches (values kept, may not apply cleanly at run time):
                      <ul className="mt-0.5 list-disc pl-5">
                        {uploadStatus.warnings.map((w) => (
                          <li key={w.key}>
                            <span className="font-mono">{w.key}</span> — {w.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            )}
            <div className="space-y-3">
              {activeVariables.map((v) => (
                <VariableRow
                  key={v.param_name}
                  variable={v}
                  value={values[v.param_name]}
                  onChange={(newVal) =>
                    setValues((prev) => ({ ...prev, [v.param_name]: newVal }))
                  }
                />
              ))}
            </div>
          </div>
        ) : editKeysLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Icons.Spinner className="h-4 w-4 animate-spin" /> Loading keys…
          </div>
        ) : (!isEdit && testcasekey) || isEdit ? (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            The picked test case has no dynamic keys — the Datasource will be empty.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  View datasource modal (read-only)                                   */
/* ------------------------------------------------------------------ */
function ViewDatasourceModal({ datasource, onClose }) {
  const entries = Object.entries(flattenLegacyMatrix(datasource.data));
  return (
    <Modal title="Datasource details" onClose={onClose}>
      <dl className="space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Name</dt>
          <dd className="font-medium text-slate-900">{datasource.name}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Test case</dt>
          <dd className="text-right font-medium text-slate-900">{datasource.testcase_name || "—"}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Created By</dt>
          <dd className="font-medium text-slate-900">{datasource.created_by || "—"}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Created Date</dt>
          <dd className="font-medium text-slate-900">{formatDate(datasource.createddate)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Modified Date</dt>
          <dd className="font-medium text-slate-900">{formatDate(datasource.modifieddate)}</dd>
        </div>
      </dl>
      <div className="mt-4 border-t border-slate-100 pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Values ({entries.length})
        </p>
        {entries.length === 0 ? (
          <p className="text-sm text-slate-500">No keys.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {entries.map(([k, v]) => (
                  <tr key={k}>
                    <td className="w-1/3 bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-600">{k}</td>
                    <td className="px-3 py-2 text-slate-800">
                      {v === "" || v == null ? (
                        <span className="italic text-slate-400">(empty — will prompt at run time)</span>
                      ) : (
                        String(v)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  Confirm modal                                                       */
/* ------------------------------------------------------------------ */
/* No / Yes, as every confirmation (ConfirmModal.js). */
function ConfirmModal({ title, message, onCancel, onConfirm, busy }) {
  return (
    <Modal
      fit
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            No
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Working…" : "Yes"}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-700">{message}</p>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  Row action button                                                   */
/* ------------------------------------------------------------------ */
function ActionButton({ icon: Glyph, label, onClick, danger = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={[
        "inline-flex h-8 w-8 items-center justify-center rounded-lg border text-sm transition disabled:cursor-not-allowed disabled:opacity-50",
        danger
          ? "border-rose-200 bg-card text-rose-500 hover:border-rose-300 hover:bg-rose-50"
          : "border-slate-200 bg-card text-slate-500 hover:border-slate-300 hover:text-slate-800",
      ].join(" ")}
    >
      <Glyph className="h-4 w-4" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Datasource view                                                     */
/* ------------------------------------------------------------------ */
export default function DatasourceView() {
  const { userkey } = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [formMode, setFormMode] = useState(null); // "add" | "edit" | null
  const [editDs, setEditDs] = useState(null);
  const [viewDs, setViewDs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const body = await fetchJson(`${API_BASE_URL}datasources`);
      const list = Array.isArray(body?.data) ? body.data : [];
      setRows(list);
      setSelected((prev) => {
        const keys = new Set(list.map((r) => r.datasourcekey));
        const next = new Set();
        prev.forEach((k) => keys.has(k) && next.add(k));
        return next;
      });
    } catch (e) {
      setLoadError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(q) ||
          (r.testcase_name || "").toLowerCase().includes(q)
      )
    : rows;

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [pageCount, page]);

  const toggleOne = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAll = () => {
    if (filtered.length === 0) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const allInSelection = filtered.every((r) => next.has(r.datasourcekey));
      if (allInSelection) filtered.forEach((r) => next.delete(r.datasourcekey));
      else filtered.forEach((r) => next.add(r.datasourcekey));
      return next;
    });
  };

  const startAdd = () => {
    setEditDs(null);
    setFormError("");
    setFormMode("add");
  };

  const startEdit = (ds) => {
    setEditDs(ds);
    setFormError("");
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setEditDs(null);
    setFormError("");
  };

  const submitForm = async (payload) => {
    setSaving(true);
    setFormError("");
    try {
      if (formMode === "edit" && editDs) {
        await fetchJson(`${API_BASE_URL}datasources/${editDs.datasourcekey}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: payload.name, data: payload.data }),
        });
      } else {
        await fetchJson(`${API_BASE_URL}datasources`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            testcasekey: payload.testcasekey,
            name: payload.name,
            data: payload.data,
            userkey: userkey || null,
          }),
        });
      }
      closeForm();
      await reload();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.action();
      setConfirm(null);
      await reload();
    } catch (e) {
      alert(e.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const askDeleteOne = (ds) => {
    setConfirm({
      title: "Delete Datasource",
      message: `Delete Datasource "${ds.name}"? This cannot be undone.`,
      confirmLabel: "Delete",
      action: async () => {
        await fetchJson(`${API_BASE_URL}datasources/${ds.datasourcekey}`, {
          method: "DELETE",
        });
      },
    });
  };

  const askDeleteSelected = () => {
    const keys = Array.from(selected);
    if (keys.length === 0) return;
    setConfirm({
      title: "Delete selected Datasource",
      message: `Delete ${keys.length} selected Datasource${keys.length === 1 ? "" : "s"}? This cannot be undone.`,
      confirmLabel: `Delete ${keys.length}`,
      action: async () => {
        await fetchJson(`${API_BASE_URL}datasources/bulk-delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ datasourcekeys: keys }),
        });
        setSelected(new Set());
      },
    });
  };

  const allSelected =
    filtered.length > 0 && filtered.every((r) => selected.has(r.datasourcekey));
  const someSelected = selected.size > 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Datasource</h1>
          {/* <p className="mt-1 text-sm text-slate-500">
            JSON Datasource that feed dynamic values into test cases. Pick one at run time to skip the value prompts.
          </p> */}
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-card text-slate-500 transition hover:border-slate-400 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icons.Refresh className={["h-4 w-4", loading ? "animate-spin" : ""].join(" ")} />
            </button>
            <button
              type="button"
              onClick={askDeleteSelected}
              disabled={!someSelected}
              title={someSelected ? `Delete ${selected.size} selected` : "Select rows to delete"}
              aria-label="Delete selected Datasource"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 bg-card text-rose-500 transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icons.Trash className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={startAdd}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800"
          >
            <Icons.Plus className="h-4 w-4" />
            Add Datasource
          </button>
          <HelpButton screen="datasource" />
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by datasource name or test case…"
            aria-label="Search Datasource"
            className="w-full rounded-lg border border-slate-300 bg-card py-2 pl-9 pr-9 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {loadError}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
          <Icons.Spinner className="mx-auto mb-2 h-5 w-5 animate-spin" />
          Loading Datasource…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
          No Datasource yet. Save a test case with dynamic steps (auto-creates a Template) or click "Add Datasource".
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all Datasource"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                    />
                  </th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Test case</th>
                  <th className="px-4 py-3">Keys</th>
                  <th className="px-4 py-3">Created Date</th>
                  <th className="px-4 py-3">Modified Date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                      No Datasource match "{search}".
                    </td>
                  </tr>
                ) : (
                  pageRows.map((ds) => {
                    const isChecked = selected.has(ds.datasourcekey);
                    const keyCount = Object.keys(ds.data || {}).length;
                    return (
                      <tr
                        key={ds.datasourcekey}
                        className={[
                          "group border-t border-slate-100",
                          isChecked ? "bg-brand-50/40" : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <td className="px-4 py-3 align-middle">
                          <input
                            type="checkbox"
                            aria-label={`Select ${ds.name}`}
                            checked={isChecked}
                            onChange={() => toggleOne(ds.datasourcekey)}
                            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                          />
                        </td>
                        <td className="px-4 py-3 align-middle text-sm font-medium text-slate-900">
                          {ds.name}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-700">
                          {ds.testcase_name || "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-600">{keyCount}</td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-500">
                          {formatDate(ds.createddate)}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-500">
                          {formatDate(ds.modifieddate)}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center justify-end gap-1.5">
                            <ActionButton icon={Icons.Eye} label="View" onClick={() => setViewDs(ds)} />
                            <ActionButton icon={Icons.Pencil} label="Edit" onClick={() => startEdit(ds)} />
                            <ActionButton
                              icon={Icons.Trash}
                              label="Delete"
                              danger
                              onClick={() => askDeleteOne(ds)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
              <span>
                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  title="Previous page"
                  aria-label="Previous page"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-card text-slate-500 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icons.ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-sm text-slate-600">
                  Page {currentPage} of {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={currentPage === pageCount}
                  title="Next page"
                  aria-label="Next page"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-card text-slate-500 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icons.ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {formMode && (
        <DatasourceFormModal
          mode={formMode}
          initial={editDs}
          onClose={closeForm}
          onSubmit={submitForm}
          saving={saving}
          error={formError}
        />
      )}

      {viewDs && <ViewDatasourceModal datasource={viewDs} onClose={() => setViewDs(null)} />}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          busy={confirmBusy}
          onCancel={() => (!confirmBusy ? setConfirm(null) : null)}
          onConfirm={runConfirm}
        />
      )}
    </div>
  );
}
